import type { PayrollItem, PayrollItemCategory } from '@/mocks/db/types';

/**
 * Payroll items 一覧の side panel の枠。
 *
 * `PayrollItemCategory` は 9 値だが、side panel は 8 枠。`references` の枠が
 * `references`（参照）と `inquiry`（照会）の両方を拾う。両者を category として
 * 統合しないのは、追加フローの「照会先」必須判定と対応表カードの表示切替が
 * この区別に依存しているため（`payrollItemAddDraft` / `PayrollItemLookupTableCard`）。
 *
 * ライフサイクル（適用中 / 適用予定 / 下書き / アーカイブ）はこの軸に載せない。
 * 一覧上部のステータスタブ（`payroll-structure-unit-list-tab`）が担当する。
 */
export type PayrollItemCategoryTabKey = Exclude<PayrollItemCategory, 'inquiry'>;
export const PAYROLL_ITEM_CATEGORY_TAB_KEYS = ['earnings', 'deductions', 'aggregates', 'adjustments', 'employerContributions', 'reimbursements', 'paymentsInKind', 'references'] as const satisfies readonly PayrollItemCategoryTabKey[];
export const EMPTY_PAYROLL_ITEM_CATEGORY_TAB_COUNTS: Record<PayrollItemCategoryTabKey, number> = {
  earnings: 0,
  deductions: 0,
  aggregates: 0,
  adjustments: 0,
  employerContributions: 0,
  reimbursements: 0,
  paymentsInKind: 0,
  references: 0
};
type PayrollItemListTabRow = Pick<PayrollItem, 'category' | 'additionalCategories'>;

/**
 * その item が属する枠をすべて返す。主分類に加えて追加分類も枠として数える。
 *
 * 集計項目のうち総支給額・控除合計のように「支給欄・控除欄に並ぶ金額の合計」である
 * ものは、集計タブと支給/控除タブの両方に出る。**そのため枠ごとの件数の合計は
 * item の総数より多くなる**（1 件が複数の枠に数えられる）。
 */
function payrollItemCategoryTabsOf(item: PayrollItemListTabRow): PayrollItemCategoryTabKey[] {
  return [...new Set([item.category, ...(item.additionalCategories ?? [])].map(resolvePayrollItemCategoryTab))];
}

/** category が属する side panel の枠。`inquiry` だけ枠名と一致しない。 */
export function resolvePayrollItemCategoryTab(category: PayrollItemCategory): PayrollItemCategoryTabKey {
  return category === 'inquiry' ? 'references' : category;
}
export function countPayrollItemCategoryTabs(items: readonly PayrollItemListTabRow[]): Record<PayrollItemCategoryTabKey, number> {
  const counts = {
    ...EMPTY_PAYROLL_ITEM_CATEGORY_TAB_COUNTS
  };
  for (const item of items) {
    for (const tab of payrollItemCategoryTabsOf(item)) {
      counts[tab]++;
    }
  }
  return counts;
}
export function matchesPayrollItemCategoryTab(item: PayrollItemListTabRow, tab: PayrollItemCategoryTabKey): boolean {
  return payrollItemCategoryTabsOf(item).includes(tab);
}